// ============================================================================
// Tax Number Validation — Country-Specific
// Rule 15: Single source of truth, used by backend service + frontend Zod schemas
// ============================================================================

/**
 * Country-specific tax number regex patterns.
 * Add new countries here — validator picks them up automatically.
 */
export interface TaxNumberPattern {
  regex: RegExp;
  format: string;
  label: string;
  required: boolean;
  /** Characters to strip before validation. Defaults to whitespace and dashes. */
  stripChars?: RegExp;
}

export const TAX_NUMBER_PATTERNS: Record<string, TaxNumberPattern> = {
  US: {
    regex: /^\d{2}-?\d{7}$/,
    format: 'XX-XXXXXXX',
    label: 'EIN (Employer Identification Number)',
    required: false,
  },
  TR: {
    regex: /^\d{10}$/,
    format: '10 digits',
    label: 'VKN (Vergi Kimlik Numarası)',
    required: true,
  },
  DE: {
    regex: /^\d{11}$/,
    format: '11 digits',
    label: 'Steuernummer',
    required: true,
  },
  CA: {
    regex: /^\d{9}$/,
    format: '9 digits',
    label: 'BN (Business Number)',
    required: false,
  },
  MX: {
    regex: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/,
    format: 'RFC format',
    label: 'RFC (Registro Federal de Contribuyentes)',
    required: true,
    stripChars: /\s/g,
  },
};

export interface TaxValidationResult {
  isValid: boolean;
  format?: string;
  message?: string;
}

/**
 * Validate a tax number for a given country.
 * Returns isValid:true for unsupported countries (no validation applied).
 */
export function validateTaxNumber(taxNumber: string, countryCode: string): TaxValidationResult {
  const pattern = TAX_NUMBER_PATTERNS[countryCode.toUpperCase()];

  if (!pattern) {
    return { isValid: true };
  }

  const stripRegex = pattern.stripChars ?? /[\s-]/g;
  const cleanNumber = taxNumber.replace(stripRegex, '');

  if (!pattern.regex.test(cleanNumber)) {
    return {
      isValid: false,
      format: pattern.format,
      message: `${pattern.label} must be in format: ${pattern.format}`,
    };
  }

  return { isValid: true, format: pattern.format };
}

/**
 * Get tax number configuration for a country.
 * Returns undefined for unsupported countries.
 */
export function getTaxNumberConfig(countryCode: string) {
  return TAX_NUMBER_PATTERNS[countryCode.toUpperCase()];
}
